import { A } from '@ember/array';
import { Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { ASSIGNMENT } from "../../../../models/assignment";
import { BLAST_COMMAND_CONSTANTS } from "../../../commands/blast-commands";
import { ICON_SORTS } from "../../../../constants/lsg";
import { LOCATION } from 'sh-www/models/location';
import { createDeliverable } from 'sh-www/components/seller-postings/seller-posting-deliverables-input';

export default Service.extend({
  /**
   * Register services
   */
  assignmentCommandService: service('commands/assignment-commands'),
  blastCommandService: service('commands/blast-commands'),
  errorTracker: service('error-tracker'),
  moment: service(),
  session: service(),
  state: service('states/routes/admin/blaster'),
  store: service(),
  userCommandService: service('commands/user-commands'),

  currentUser: readOnly('session.currentUser'),

  /**
   * Send a blast for this assignment.
   *
   * @return {Ember.RSVP.Promise}
   */
  blastTheHunt() {
    return new Promise((resolve, reject) => {
      if (this.get('state.isSendingBlast')) {
        return Promise.reject(false);
      }

      this.set('state.isSendingBlast', true);

      let blastResultsToSend = A(),
        customMessage = this.get('state.customMessage'),
        selectedBlastIds = this.get('state.updatedSelectedBlastResultIds');

      selectedBlastIds.forEach(blastResultId => {
        blastResultsToSend.pushObject(this.get('store').peekRecord('freelancerSearchResult', blastResultId));
      });

      this.get('blastCommandService').blastTheHunt(
        this.get('state.currentAssignment'),
        blastResultsToSend,
        this._getBlastParamsFromState(),
        customMessage
      ).then(() => {

        blastResultsToSend.forEach(blastResult => {
          let notifiedCount = blastResult.get('userNotifiedCount');
          if (notifiedCount) {
            blastResult.set('userNotifiedCount', notifiedCount + 1);
          } else {
            blastResult.set('userNotifiedCount', 1);
          }
        });

        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error('blastTheHunt -- error blast hunt state manager', this, e);
        reject(e);
      }).finally(() => this.set('state.isSendingBlast', false));
    });
  },

  /**
   * change the sort for the specific sort name for 'filtered' results.
   *
   * @param {String} sortName     ADMIN_BLASTER__SORT_NAMES.*
   */
  filteredResultsSortChanged(sortName) {
    if (sortName === this.get('state.sortFilteredResultsName')) {
      let newSort = this.get('state.sortFilteredResultsDirection') === ICON_SORTS.SORT_DESCENDING ?
        ICON_SORTS.SORT_ASCENDING :
        ICON_SORTS.SORT_DESCENDING;

      this.set('state.sortFilteredResultsDirection', newSort);
    } else {
      this.set('state.sortFilteredResultsDirection', ICON_SORTS.SORT_DESCENDING);
    }

    this.set('state.sortFilteredResultsName', sortName);
  },

  /**
   * Initialize our state for a new assignment creation.
   *
   * This will NOT clear existing items that should be static b/t assignments (e.g. countries, regions, and services)
   */
  initStateManager() {
    this.get('state').init();
  },

  /**
   * Load all the active assignments currently on SH.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadActiveAssignments() {
    let activePostings = this.get('state.activePostings');
    this.set('state.isLoadingAllAssignments', true);
    if (activePostings.get('length')) {
      this.set('state.isLoadingAllAssignments', false);
      return Promise.resolve(activePostings);
    }

    this.set('state.isLoadingAllAssignments', true);
    return new Promise((resolve, reject) => {
      this.get('assignmentCommandService').loadAssignmentsDefault(ASSIGNMENT.STATUS.LIVE, 1, 10, undefined, ['assignment-invites']).then(assignemnts => {
        this.set('state.activePostings', assignemnts);
        resolve(assignemnts);
      }).catch(e => {
        this.get('errorTracker').error('loadActiveAssignments -- error loading active assignments', this, e);
        reject(e);
      }).finally(() => this.set('state.isLoadingAllAssignments', false));
    });
  },

  /**
   * Load an assignment id, or pull it out of the cache.
   *
   * @param {String} assignmentId
   * @return {Ember.RSVP.Promise}
   */
  loadAssignment(assignmentId) {
    let currentAssignment = this.get('state.currentAssignment');
    if (currentAssignment && currentAssignment.get('id') === assignmentId) {
      return Promise.resolve(currentAssignment);
    }

    let includes = [
      'blasts',
      'buyer.company',
      'services',
      'candidate-location.country',
      'candidate-location.region.countries',
      'location.country',
      'location.region.countries',
    ];
    return new Promise((resolve, reject) => {
      this.get('assignmentCommandService').loadAssignment(assignmentId, includes).then(assignment => {
        this.set('state.currentAssignment', assignment);
        resolve(assignment);
      }).catch(e => {
        this.get('errorTracker').error('loadAssignment -- error loading assignment', this, e);
        reject(e);
      });
    });
  },

  /**
   * Load the countries if they aren't already in memory.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadCountries() {
    let countries = this.get('state.availableCountries');
    if (countries.get('length')) {
      return Promise.resolve(countries);
    }

    return this.get('store').findAll('country', {
      adapterOptions : {
        isPublic : true
      }
    }).then(countries => this.set('state.availableCountries', countries));
  },

  /**
   * Load the countries if they aren't already in memory.
   *
   * @return {Ember.RSVP.Promise}
   */
   loadEquipment() {
    const equipment = this.state.availableEquipment;
    if (equipment.length) {
      return Promise.resolve(equipment);
    }

    return this.store.findAll('equipment', {
      adapterOptions : {
        isPublic : true
      }
    }).then(equipment => this.set('state.availableEquipment', equipment));
  },

  /**
   * Load the countries if they aren't already in memory.
   *
   * @return {Ember.RSVP.Promise}
   */
   loadLanguages() {
    const languages = this.state.availableLanguages;
    if (languages.length) {
      return Promise.resolve(languages);
    }

    return this.store.findAll('language', {
      adapterOptions : {
        isPublic : true
      }
    }).then(languages => this.set('state.availableLanguages', languages));
  },

  /**
   * Load the regions if they aren't already in memory.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadRegions() {
    let regions = this.get('state.availableRegions');
    if (regions.get('length')) {
      return Promise.resolve(regions);
    }

    return this.get('store').findAll('region', {
      adapterOptions : {
        include : 'countries',
        isPublic : true
      }
    }).then(regions => this.set('state.availableRegions', regions));
  },

  /**
   * Load all the services from the DB.
   *
   * @return {Ember.RSVP.Promise<Ember.MutableArray<Service>>}
   */
  loadServices() {
    let availableServices = this.get('state.availableServices');
    if (availableServices.get('length')) {
      return Promise.resolve(availableServices);
    }

    return new Promise((resolve, reject) => {
      this.get('store').findAll('service', {
        adapterOptions : {
          isPublic : true
        }
      }).then(services => {
        this.set('state.availableServices', services);
        resolve(services);
      })
        .catch(e => {
          this.get('errorTracker').error('loadServices - could not load services', e, this);
          reject(e);
        });
    });
  },

  /**
   * change the sort for the specific sort name for good results.
   *
   * @param {String} sortName     ADMIN_BLASTER__SORT_NAMES.*
   */
  resultsSortChanged(sortName) {
    if (sortName === this.get('state.sortResultsName')) {
      let newSort = this.get('state.sortResultsDirection') === ICON_SORTS.SORT_DESCENDING ?
        ICON_SORTS.SORT_ASCENDING :
        ICON_SORTS.SORT_DESCENDING;

      this.set('state.sortResultsDirection', newSort);
    } else {
      this.set('state.sortResultsDirection', ICON_SORTS.SORT_DESCENDING);
    }

    this.set('state.sortResultsName', sortName);
  },

  /**
   * Search for the blast results using the input set by the admin.
   *
   * @return {Ember.RSVP.Promise}
   */
  searchBlastResults() {
    if (this.get('state.isLoadingBlastResults')) {
      return Promise.reject(false);
    }

    this.set('state.isLoadingBlastResults', true);
    this.get('state.updatedSelectedBlastResultIds').clear();
    this.set('state.freelancerBlastSearchResultCollection', null);

    return new Promise((resolve, reject) => {
      this.get('blastCommandService').loadBlastResults(
        this.get('state.currentAssignment'),
        this._getBlastParamsFromState()
      ).then(freelancerSearchResultCollection => {
        this.set('state.freelancerBlastSearchResultCollection', freelancerSearchResultCollection);
        resolve(freelancerSearchResultCollection);
      }).catch(e => {
        this.get('errorTracker').error('searchBlastResults - error blast results', e, this);
        reject(e);
      }).finally(() => this.set('state.isLoadingBlastResults', false));
    });
  },

  /**
   * Update the blast distance state
   *
   * @param {Number} blastDistance      of type ADMIN_BLASTER__DISTANCES_AVAILABLE
   */
  updateBlastDistance(blastDistance) {
    this.set('state.updatedBlastDistance', blastDistance);
  },

  /**
   * Given the currentAssignment in the state, update what we can based on that.
   */
  updateBlastStateFromAssignment() {
    const assignment = this.state.currentAssignment;

    if (!assignment) {
      return;
    }

    // update location
    switch (assignment.candidateLocation.locationType) {
      case LOCATION.TYPES.REGION:
        this.updateSelectedCountries(
          A(
            assignment.candidateLocation.region.countries.map(function (country) {
              return {
                title: country.name,
                value: country.id,
              };
            })
          )
        );
        break;
      case LOCATION.TYPES.COUNTRY:
        this.updateSelectedCountries(
          A([
            {
              title: assignment.candidateLocation.country.name,
              value: assignment.candidateLocation.country.id,
            },
          ])
        );
        break;
      default:
        this.updateCityLocation(assignment.candidateLocation);
        break;
    }

    // update services.
    assignment.get('services').forEach(service => {
      this.updateToggleServiceSelected(service.get('id'), false);
    });

    // update select status
    const assignmentSelect = assignment.get('select');

    if (assignmentSelect === true) {
      this.updateSelectStatus(1);
    } else {
      this.updateSelectStatus(null);
    }
  },

  /**
   * Update the city Location record with a new location.
   *
   * @param {Location|null} location
   */
  updateCityLocation(location) {
    this.set('state.updatedCityLocation', location);

    if (location) {
      this.updateSelectedCountries(A(), true);
    }
  },

  /**
   * Update the whether the search is country restriced
   * @param {Boolean} isCountryRestricted
   */
   updateCountryRestricted(isCountryRestricted) {
    this.set('state.updatedCountryRestricted', isCountryRestricted);
  },

  /**
   * Register a handler to handle a transition when a new assignment has been selected.
   *
   * @param {Function} callback
   */
  registerOnNewPostingSelectedTransition(callback) {
    this.set('state.onNewPostingSelectedTransition', callback);
  },

  /**
   * Update our state to set all items as checked.
   *
   * @param {Boolean} isSelected    Set to true to select all.
   */
  updateSelectAllResultsChecked(isSelected) {
    this.set('state.isSelectAllResultsChecked', isSelected);

    let updatedSelectedBlastResultIds = this.get('state.updatedSelectedBlastResultIds'),
      blastResults = this.get('state.blastResults');

    updatedSelectedBlastResultIds.clear();

    if (!isSelected) {
      return;
    }

    blastResults.forEach(blastResult => {
      updatedSelectedBlastResultIds.pushObject(blastResult.get('id'));
    });
  },

  /**
   * Update whether or not a blast result has been selected.
   *
   * @param {Boolean} isSelected
   * @param {String} blastResultId
   */
  updateSelectBlastResultToggled(isSelected, blastResultId) {
    let updatedSelectedBlastResultIds = this.get('state.updatedSelectedBlastResultIds');

    if (isSelected) {
      updatedSelectedBlastResultIds.pushObject(blastResultId);
      return;
    }

    updatedSelectedBlastResultIds.removeObject(blastResultId);
  },

  /**
   * Update the selected countries that the user has picked.
   *
   * @param {Ember.MutableArray<Object>} countryOptions   The options for the multi-select that the user has selected.
   * @param {Boolean=} opt_isReplace    True if you want to replace all selected.
   */
  updateSelectedCountries(countryOptions, opt_isReplace) {
    let updatedCountryOptions = this.get('state.updatedCountryOptions');
    if (opt_isReplace) {
      updatedCountryOptions.clear();
    }

    updatedCountryOptions.addObjects(countryOptions);

    if (countryOptions.get('length')) {
      this.updateCityLocation(null);
    }
  },

  removeSelectedCountry(countryOption) {
    this.state.updatedCountryOptions.removeObject(countryOption);
  },

  removeSelectedEquipment(equipmentOption) {
    const updatedEquipmentOptions = this.state.updatedEquipmentOptions;
    this.set('state.updatedEquipmentOptions', updatedEquipmentOptions.filter(({value}) => value !== equipmentOption.value));
  },

  addSelectedEquipment(equipmentOption) {
    const updatedEquipmentOptions = this.state.updatedEquipmentOptions;
    if (!updatedEquipmentOptions.includes(equipmentOption)) {
      this.set('state.updatedEquipmentOptions', [...updatedEquipmentOptions, equipmentOption]);
    }
  },

  clearEquipment() {
    this.set('state.updatedEquipmentOptions', []);
  },

  removeSelectedLanguage(languageOption) {
    const updatedLanguageOptions = this.state.updatedLanguageOptions;
    this.set('state.updatedLanguageOptions', updatedLanguageOptions.filter(({value}) => value !== languageOption.value));
  },

  addSelectedLanguage(languageOption) {
    const updatedLanguageOptions = this.state.updatedLanguageOptions;
    if (!updatedLanguageOptions.includes(languageOption)) {
      this.set('state.updatedLanguageOptions', [...updatedLanguageOptions, languageOption]);
    }
  },

  clearLanguages() {
    this.set('state.updatedLanguageOptions', []);
  },


  /**
   * Update the minimum rating of freelancer for the blast.  from 0 to ADMIN_BLASTER__STAR_FILTER_COUNT
   *
   * @param {Number} minRatingSelected
   */
  updateSelectedMinRating(minRatingSelected) {
    this.set('state.updatedSelectedMinRating', parseInt(minRatingSelected));
  },

  /**
   * Update the select status
   *
   * @param {Number} selectStatus
   */
  updateSelectStatus(selectStatus) {
    this.set('state.updatedSelectStatus', selectStatus);
  },

  /**
   * Update the service search type
   *
   *
   */
   updateServiceSearchType(searchType) {
    this.set('state.updatedServiceSearchType', searchType);
  },

  /**
   * Toggle the isLoadingInitial state.
   *
   * @param {Boolean} isLoading
   */
  updateToggleIsLoadingInitialState(isLoading) {
    this.set('state.isLoadingInitialState', isLoading);
  },

  /**
   * Toggle the state of the selected service id checkbox.
   *
   * @param {String} serviceId
   * @param {Boolean} isSelected    This is the CURRENT state.  so if selected, we want to de-select and vice versa
   */
  updateToggleServiceSelected(serviceId, isSelected) {
    let selectedServiceIds = this.get('state.updatedSelectedServiceIds');
    if (!isSelected) {
      selectedServiceIds.pushObject(serviceId);
      return;
    }

    this.set(
      'state.updatedSelectedServiceIds',
      selectedServiceIds.filter(selectedServiceId => selectedServiceId !== serviceId));
  },

  /**
   * Get an object representing the current blast state.
   *
   * @return {{}}       with keys of BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.*
   * @private
   */
  _getBlastParamsFromState() {
    const params = {
        [BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.DISTANCE] : this.state.updatedBlastDistance,
        [BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.MIN_RATING] : this.state.updatedSelectedMinRating,
        [BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.SERVICES_SEARCH_TYPE] : this.state.updatedServiceSearchType,
        [BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.EXPAND_TO_LIKE_SERVICES] : false,
        [BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.IS_COUNTRY_RESTRICTED] : this.state.updatedCountryRestricted,
        [BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.SELECT_STATUS] : this.state.updatedSelectStatus,
      };

    const services = this.state.updatedSelectedServiceIds;

    if (services.length) {
      params[BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.SERVICES] = services;
    }

    const location = this.state.updatedCityLocation;
    const countries = this.state.updatedSelectedCountries;

    if (location) {
      params[BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.PLACE_ID] = location.placeId;
    } else if (countries.length) {
      params[BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.COUNTRY_ID] = countries.map(country => country.id);
    }

    const languages = this.state.updatedLanguageOptions;
    if (languages.length) {
      params[BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.LANGUAGES] = languages.map(language => language.value);
    }

    const equipment = this.state.updatedEquipmentOptions;
    if (equipment.length) {
      params[BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.EQUIPMENT] = equipment.map(equipment => equipment.value);
    }

    params[BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS.SELECT_STATUS] = this.state.updatedSelectStatus;

    /**
     * This is here because our backend code required a 'deliverables' field to validate that the assignment is
     * valid.  Rather than overly complicate the backend code, we'll leave this deliverables param to be sent
     * down to pass validation for our internal blast tool.
     * Validation requires 10 characters minimum.
     */
    params['assignment_deliverables'] = [ createDeliverable('take out - 10 character minimum') ];
    return params;
  }
});
